import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, fromEvent, timer, of } from "rxjs";
import { map, filter, debounceTime, distinctUntilChanged, switchMap, catchError } from "rxjs/operators";

export interface LazyLoadConfig {
  enabled: boolean;
  preloadDistance: number;
  preloadDelay: number;
  maxPreloads: number;
  cacheSize: number;
  retryAttempts: number;
  retryDelay: number;
}

export interface LazyLoadItem {
  id: string;
  element: HTMLElement;
  loaded: boolean;
  loading: boolean;
  error: boolean;
  priority: number;
  timestamp: number;
  retryCount: number;
}

export interface PreloadStrategy {
  name: string;
  priority: number;
  condition: (element: HTMLElement) => boolean;
  action: (element: HTMLElement) => Promise<void>;
}

@Injectable({
  providedIn: "root",
})
export class LazyLoadingService {
  private itemsSubject = new BehaviorSubject<LazyLoadItem[]>([]);
  private config: LazyLoadConfig = {
    enabled: true,
    preloadDistance: 1000, // pixels
    preloadDelay: 100, // ms
    maxPreloads: 10,
    cacheSize: 100,
    retryAttempts: 3,
    retryDelay: 1000,
  };

  private preloadStrategies: PreloadStrategy[] = [];
  private intersectionObserver?: IntersectionObserver;
  private preloadQueue: HTMLElement[] = [];
  private loadedCache = new Map<string, any>();

  constructor() {
    this.initializeLazyLoading();
    this.setupDefaultStrategies();
  }

  /**
   * Inicializar lazy loading
   */
  private initializeLazyLoading(): void {
    if (!this.config.enabled || !("IntersectionObserver" in window)) {
      return;
    }

    this.setupIntersectionObserver();
    this.setupScrollListener();
    this.setupPreloadTimer();
  }

  /**
   * Configurar Intersection Observer
   */
  private setupIntersectionObserver(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.loadElement(entry.target as HTMLElement);
          }
        });
      },
      {
        rootMargin: `${this.config.preloadDistance}px`,
        threshold: 0.1,
      },
    );
  }

  /**
   * Configurar listener de scroll
   */
  private setupScrollListener(): void {
    fromEvent(window, "scroll")
      .pipe(debounceTime(100), distinctUntilChanged())
      .subscribe(() => {
        this.updatePreloadQueue();
      });
  }

  /**
   * Configurar timer de preload
   */
  private setupPreloadTimer(): void {
    timer(this.config.preloadDelay, this.config.preloadDelay).subscribe(() => {
      this.processPreloadQueue();
    });
  }

  /**
   * Configurar estratégias padrão
   */
  private setupDefaultStrategies(): void {
    // Estratégia para imagens
    this.addPreloadStrategy({
      name: "image",
      priority: 1,
      condition: (element) => element.tagName === "IMG" && !element.getAttribute("src"),
      action: async (element) => {
        const src = element.getAttribute("data-src");
        if (src) {
          element.setAttribute("src", src);
          element.removeAttribute("data-src");
        }
      },
    });

    // Estratégia para componentes Angular
    this.addPreloadStrategy({
      name: "component",
      priority: 2,
      condition: (element) => element.hasAttribute("data-component"),
      action: async (element) => {
        const componentName = element.getAttribute("data-component");
        if (componentName) {
          await this.preloadComponent(componentName);
        }
      },
    });

    // Estratégia para dados
    this.addPreloadStrategy({
      name: "data",
      priority: 3,
      condition: (element) => element.hasAttribute("data-endpoint"),
      action: async (element) => {
        const endpoint = element.getAttribute("data-endpoint");
        if (endpoint) {
          await this.preloadData(endpoint);
        }
      },
    });

    // Estratégia para scripts
    this.addPreloadStrategy({
      name: "script",
      priority: 4,
      condition: (element) => element.tagName === "SCRIPT" && element.hasAttribute("data-src"),
      action: async (element) => {
        const src = element.getAttribute("data-src");
        if (src) {
          await this.loadScript(src);
        }
      },
    });

    // Estratégia para estilos
    this.addPreloadStrategy({
      name: "style",
      priority: 5,
      condition: (element) => element.tagName === "LINK" && element.getAttribute("rel") === "stylesheet",
      action: async (element) => {
        const href = element.getAttribute("href");
        if (href) {
          await this.loadStylesheet(href);
        }
      },
    });
  }

  /**
   * Adicionar estratégia de preload
   */
  addPreloadStrategy(strategy: PreloadStrategy): void {
    this.preloadStrategies.push(strategy);
    this.preloadStrategies.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Observar elemento para lazy loading
   */
  observeElement(element: HTMLElement, priority: number = 1): void {
    if (!this.config.enabled || !this.intersectionObserver) {
      return;
    }

    const item: LazyLoadItem = {
      id: this.generateId(),
      element,
      loaded: false,
      loading: false,
      error: false,
      priority,
      timestamp: Date.now(),
      retryCount: 0,
    };

    this.addItem(item);
    this.intersectionObserver.observe(element);
  }

  /**
   * Parar de observar elemento
   */
  unobserveElement(element: HTMLElement): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.unobserve(element);
    }

    const items = this.itemsSubject.value;
    const updatedItems = items.filter((item) => item.element !== element);
    this.itemsSubject.next(updatedItems);
  }

  /**
   * Carregar elemento
   */
  private async loadElement(element: HTMLElement): Promise<void> {
    const item = this.getItemByElement(element);
    if (!item || item.loaded || item.loading) {
      return;
    }

    item.loading = true;
    this.updateItem(item);

    try {
      // Aplicar estratégias de preload
      for (const strategy of this.preloadStrategies) {
        if (strategy.condition(element)) {
          await strategy.action(element);
        }
      }

      item.loaded = true;
      item.loading = false;
      this.updateItem(item);

      // Remover do observer após carregar
      if (this.intersectionObserver) {
        this.intersectionObserver.unobserve(element);
      }
    } catch (error) {
      item.error = true;
      item.loading = false;
      this.updateItem(item);

      // Retry se ainda houver tentativas
      if (item.retryCount < this.config.retryAttempts) {
        item.retryCount++;
        setTimeout(() => {
          this.loadElement(element);
        }, this.config.retryDelay);
      }
    }
  }

  /**
   * Atualizar fila de preload
   */
  private updatePreloadQueue(): void {
    const items = this.itemsSubject.value;
    const visibleItems = items.filter((item) => this.isElementVisible(item.element));

    // Adicionar itens próximos à fila de preload
    const nearbyItems = items.filter((item) => !item.loaded && !item.loading && this.isElementNearby(item.element));

    this.preloadQueue = [...nearbyItems.map((item) => item.element)];
  }

  /**
   * Processar fila de preload
   */
  private async processPreloadQueue(): Promise<void> {
    if (this.preloadQueue.length === 0) {
      return;
    }

    const itemsToPreload = this.preloadQueue.splice(0, this.config.maxPreloads);

    for (const element of itemsToPreload) {
      await this.preloadElement(element);
    }
  }

  /**
   * Preload elemento
   */
  private async preloadElement(element: HTMLElement): Promise<void> {
    try {
      for (const strategy of this.preloadStrategies) {
        if (strategy.condition(element)) {
          await strategy.action(element);
        }
      }
    } catch (error) {}
  }

  /**
   * Verificar se elemento está visível
   */
  private isElementVisible(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    return rect.top < windowHeight && rect.bottom > 0 && rect.left < windowWidth && rect.right > 0;
  }

  /**
   * Verificar se elemento está próximo
   */
  private isElementNearby(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const distance = this.config.preloadDistance;

    return rect.top < windowHeight + distance && rect.bottom > -distance;
  }

  /**
   * Preload componente Angular
   */
  private async preloadComponent(componentName: string): Promise<void> {
    if (this.loadedCache.has(componentName)) {
      return;
    }

    try {
      // Implementar preload de componente
      const module = await import(`../features/${componentName}/${componentName}.module`);
      this.loadedCache.set(componentName, module);
    } catch (error) {}
  }

  /**
   * Preload dados
   */
  private async preloadData(endpoint: string): Promise<void> {
    if (this.loadedCache.has(endpoint)) {
      return;
    }

    try {
      const response = await fetch(endpoint);
      const data = await response.json();
      this.loadedCache.set(endpoint, data);
    } catch (error) {}
  }

  /**
   * Carregar script
   */
  private async loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  /**
   * Carregar stylesheet
   */
  private async loadStylesheet(href: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.onload = () => resolve();
      link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
      document.head.appendChild(link);
    });
  }

  /**
   * Adicionar item
   */
  private addItem(item: LazyLoadItem): void {
    const items = this.itemsSubject.value;
    const newItems = [...items, item];
    this.itemsSubject.next(newItems);
  }

  /**
   * Atualizar item
   */
  private updateItem(item: LazyLoadItem): void {
    const items = this.itemsSubject.value;
    const index = items.findIndex((i) => i.id === item.id);
    if (index !== -1) {
      items[index] = item;
      this.itemsSubject.next([...items]);
    }
  }

  /**
   * Obter item por elemento
   */
  private getItemByElement(element: HTMLElement): LazyLoadItem | undefined {
    return this.itemsSubject.value.find((item) => item.element === element);
  }

  /**
   * Obter itens
   */
  getItems(): LazyLoadItem[] {
    return this.itemsSubject.value;
  }

  /**
   * Obter observable dos itens
   */
  getItems$(): Observable<LazyLoadItem[]> {
    return this.itemsSubject.asObservable();
  }

  /**
   * Obter estatísticas
   */
  getStats(): any {
    const items = this.getItems();
    const loaded = items.filter((item) => item.loaded).length;
    const loading = items.filter((item) => item.loading).length;
    const errors = items.filter((item) => item.error).length;

    return {
      total: items.length,
      loaded,
      loading,
      errors,
      cacheSize: this.loadedCache.size,
      preloadQueue: this.preloadQueue.length,
      strategies: this.preloadStrategies.length,
    };
  }

  /**
   * Configurar lazy loading
   */
  configure(config: Partial<LazyLoadConfig>): void {
    this.config = { ...this.config, ...config };

    if (config.enabled !== undefined) {
      if (config.enabled) {
        this.initializeLazyLoading();
      } else {
        this.stopLazyLoading();
      }
    }
  }

  /**
   * Parar lazy loading
   */
  stopLazyLoading(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.preloadQueue = [];
    this.loadedCache.clear();
  }

  /**
   * Limpar cache
   */
  clearCache(): void {
    this.loadedCache.clear();
  }

  /**
   * Gerar ID único
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
